import React from "react";
import axios from "axios";
import {
  PaymentData,
  PaymentResultInterface,
} from "../contexts/channels/interfaces/payment-result.interface";

export const API_PROGRAM_REST_URL =
  process.env.NEXT_PUBLIC_API_PROGRAM_REST_URL ||
  process.env.REACT_APP_API_PROGRAM_REST_URL ||
  "https://kong.universa.vip/program-api-rest";

export const API_PROGRAM_REST_TOKEN =
  process.env.NEXT_PUBLIC_API_PROGRAM_REST_TOKEN ||
  process.env.REACT_APP_API_PROGRAM_REST_TOKEN ||
  "fd4ef3dcd5e92f44d00bf4c770c5dcc785fe7a35e999e5f909955b317401ff78";

export interface PaymentScopeParams {
  programId?: string;
  programCategoryId?: string;
  companyId?: string;
}

export const SELECT_PAYMENTS_COLUMNS =
  "id,name,description,account_number,image_payment,is_active,is_global,created_at,company_id";

export const createPostgrestAxiosClient = (
  baseURL = API_PROGRAM_REST_URL,
  token = API_PROGRAM_REST_TOKEN
) => {
  return axios.create({
    baseURL,
    headers: {
      "x-api-token": token,
      Accept: "application/json",
      "Content-Type": "application/json",
    },
  });
};

/**
 * Resolves image URL from image_payment attribute with fallback to defaultThumbnail
 */
export const getPaymentImageUrl = (
  imagePayment?: string | null,
  defaultThumbnail?: string
): string | null => {
  if (!imagePayment || imagePayment.trim() === "" || imagePayment === "{}") {
    if (defaultThumbnail) {
      if (
        defaultThumbnail.startsWith("http://") ||
        defaultThumbnail.startsWith("https://")
      ) {
        return defaultThumbnail;
      }
      return `https://rustic.universa.vip/images/${defaultThumbnail}`;
    }
    return null;
  }

  const trimmed = imagePayment.trim();

  // If already a full URL
  if (trimmed.startsWith("http://") || trimmed.startsWith("https://")) {
    return trimmed;
  }

  // If JSON object or array format like {"path":"./logo.png"} or [{"path":"./logo.png"}]
  if (
    (trimmed.startsWith("{") && trimmed.endsWith("}")) ||
    (trimmed.startsWith("[") && trimmed.endsWith("]"))
  ) {
    try {
      const parsed = JSON.parse(trimmed);
      const target = Array.isArray(parsed) ? parsed[0] : parsed;
      if (typeof target === "string") {
        return getPaymentImageUrl(target, defaultThumbnail);
      }
      if (target && typeof target === "object") {
        const filePath =
          target.url ||
          target.path ||
          target.relativePath ||
          target.name ||
          "";
        if (filePath) {
          if (
            filePath.startsWith("http://") ||
            filePath.startsWith("https://")
          ) {
            return filePath;
          }
          const cleanPath = filePath.replace(/^\.?\//, "");
          if (cleanPath) {
            const assetsBaseUrl =
              process.env.REACT_APP_ASSETS_URL ||
              "https://storagev2.universa.vip/assets";
            return `${assetsBaseUrl}/${cleanPath}`;
          }
        }
      }
    } catch (e) {
      // ignore parse error
    }
  }

  // If plain filename or relative path
  const clean = trimmed.replace(/^\.?\//, "");
  if (clean) {
    const assetsBaseUrl =
      process.env.REACT_APP_ASSETS_URL ||
      "https://storagev2.universa.vip/assets";
    return `${assetsBaseUrl}/${clean}`;
  }

  return defaultThumbnail
    ? `https://rustic.universa.vip/images/${defaultThumbnail}`
    : null;
};

/**
 * Resolves payment channels using additive resolution:
 * 1. Global payments (is_global = true or is_global IS NULL)
 * 2. Category-scoped payments (payment_program_categories by programCategoryId)
 * 3. Program-scoped payments (payment_programs by programId)
 * Deduplicated by payment id.
 * Includes graceful fallback to global payments on relation fetch failure.
 */
export const getPaymentsByProgramScope = async (
  params?: PaymentScopeParams
): Promise<PaymentResultInterface> => {
  const companyId =
    params?.companyId || process.env.REACT_APP_COMPANY_ID || "";
  const client = createPostgrestAxiosClient();

  let globalPayments: PaymentData[] = [];

  // Step A: Fetch Global Payments
  try {
    const stepAResponse = await client.get<PaymentData[]>("/payments", {
      params: {
        company_id: `eq.${companyId}`,
        is_active: "eq.true",
        deleted_at: "is.null",
        or: "(is_global.eq.true,is_global.is.null)",
        select: SELECT_PAYMENTS_COLUMNS,
      },
    });
    globalPayments = Array.isArray(stepAResponse.data)
      ? stepAResponse.data
      : [];
  } catch (error) {
    console.error("Step A: Failed to fetch global payments:", error);
  }

  let programCategoryId = params?.programCategoryId;

  // Auto-resolve programCategoryId from programId if not directly provided
  if (params?.programId && !programCategoryId) {
    try {
      const progResponse = await client.get<
        { id: string; program_category_id: string }[]
      >("/programs", {
        params: {
          id: `eq.${params.programId}`,
          deleted_at: "is.null",
          select: "id,program_category_id",
        },
      });
      if (Array.isArray(progResponse.data) && progResponse.data.length > 0) {
        programCategoryId = progResponse.data[0]?.program_category_id;
      }
    } catch (error) {
      console.warn("Failed to auto-resolve program_category_id:", error);
    }
  }

  // If no scoped parameters and no category resolved, return global payments directly
  if (!programCategoryId && !params?.programId) {
    return {
      status: true,
      message: "Success fetching global payments",
      data: {
        items: globalPayments,
        total: globalPayments.length,
        size: globalPayments.length,
        page: 0,
      },
    };
  }

  // Scoped resolution with fallback to global payments
  const scopedPaymentIds = new Set<string>();

  // Step B: Scoped Category Payment IDs (if programCategoryId is resolved/provided)
  if (programCategoryId) {
    try {
      const stepBResponse = await client.get<{ payment_id: string }[]>(
        "/payment_program_categories",
        {
          params: {
            company_id: `eq.${companyId}`,
            program_category_id: `eq.${programCategoryId}`,
            is_active: "eq.true",
            deleted_at: "is.null",
            select: "payment_id",
          },
        }
      );
      if (Array.isArray(stepBResponse.data)) {
        for (const item of stepBResponse.data) {
          if (item?.payment_id) {
            scopedPaymentIds.add(item.payment_id);
          }
        }
      }
    } catch (error) {
      console.warn(
        "Step B: Failed to fetch category scoped payments, falling back to global:",
        error
      );
    }
  }

  // Step C: Scoped Program Payment IDs (if programId is provided)
  if (params?.programId) {
    try {
      const stepCResponse = await client.get<{ payment_id: string }[]>(
        "/payment_programs",
        {
          params: {
            company_id: `eq.${companyId}`,
            program_id: `eq.${params.programId}`,
            is_active: "eq.true",
            deleted_at: "is.null",
            select: "payment_id",
          },
        }
      );
      if (Array.isArray(stepCResponse.data)) {
        for (const item of stepCResponse.data) {
          if (item?.payment_id) {
            scopedPaymentIds.add(item.payment_id);
          }
        }
      }
    } catch (error) {
      console.warn(
        "Step C: Failed to fetch program scoped payments, falling back to global:",
        error
      );
    }
  }

  // Step D: Fetch Scoped Payment Details & Merge
  let scopedPayments: PaymentData[] = [];
  if (scopedPaymentIds.size > 0) {
    try {
      const idsArray = Array.from(scopedPaymentIds);
      const stepDResponse = await client.get<PaymentData[]>("/payments", {
        params: {
          id: `in.(${idsArray.join(",")})`,
          company_id: `eq.${companyId}`,
          is_active: "eq.true",
          deleted_at: "is.null",
          select: SELECT_PAYMENTS_COLUMNS,
        },
      });
      scopedPayments = Array.isArray(stepDResponse.data)
        ? stepDResponse.data
        : [];
    } catch (error) {
      console.warn(
        "Step D: Failed to fetch scoped payment details, fallback to global:",
        error
      );
    }
  }

  // Deduplicate by payment.id
  const paymentMap = new Map<string, PaymentData>();
  for (const item of globalPayments) {
    if (item?.id) {
      paymentMap.set(item.id, item);
    }
  }
  for (const item of scopedPayments) {
    if (item?.id) {
      paymentMap.set(item.id, item);
    }
  }

  const mergedItems = Array.from(paymentMap.values());

  return {
    status: true,
    message: "Success resolving scoped payments",
    data: {
      items: mergedItems,
      total: mergedItems.length,
      size: mergedItems.length,
      page: 0,
    },
  };
};

/**
 * Custom React Hook for scoped payments
 */
export function usePaymentsByProgramScope(params?: PaymentScopeParams) {
  const [data, setData] = React.useState<PaymentData[]>([]);
  const [loading, setLoading] = React.useState<boolean>(true);
  const [error, setError] = React.useState<any>(null);

  const fetchPayments = React.useCallback(async () => {
    setLoading(true);
    try {
      const res = await getPaymentsByProgramScope(params);
      if (res?.status && res.data?.items) {
        setData(res.data.items);
      } else {
        setData([]);
      }
      setError(null);
    } catch (err) {
      setError(err);
      setData([]);
    } finally {
      setLoading(false);
    }
  }, [params?.programId, params?.programCategoryId, params?.companyId]);

  React.useEffect(() => {
    fetchPayments();
  }, [fetchPayments]);

  return {
    data,
    loading,
    error,
    refetch: fetchPayments,
  };
}
